<template>
  <CCardBody>
    <!-- Filters -->
    <CCard class="filters mb-3" accent-color="warning">
      <CCollapse :show="true">
        <CCardBody class="p-2 d-flex flex-column flex-gap-2">
          <div class="d-flex flex-lg-row flex-column flex-gap-2">
            <!-- Country Filter -->
            <div class="w-100">
              <v-select
                class="v-select-filter"
                placeholder="Country.."
                :value="$store.state.filters.country"
                :options="authCountries"
                :reduce="c => c.value"
                :searchable="false"
                @input="countryFilter"
              />
            </div>
            <!-- Status Filter -->
            <div class="w-100">
              <v-select
                class="v-select-filter"
                placeholder="DNS Status.."
                v-model="dnsStatusFilter"
                :options="dnsStatusOptions"
                :reduce="s => s.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>
            <!-- Search -->
            <div class="w-100">
              <CInput
                class="mb-0 search"
                type="search"
                placeholder="Search by restaurant name or domain..."
                v-model="search"
                @input="debouncedSearch"
              />
            </div>
          </div>

          <div class="d-flex flex-lg-row flex-column flex-gap-2 mt-2">
            <!-- A Record Filter -->
            <div class="w-100">
              <v-select
                class="v-select-filter"
                placeholder="A Record Status.."
                v-model="dnsARecordFilter"
                :options="dnsARecordStatusOptions"
                :reduce="s => s.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>
            <!-- AAAA Record Filter -->
            <div class="w-100">
              <v-select
                class="v-select-filter"
                placeholder="AAAA Record Status.."
                v-model="dnsAAAARecordFilter"
                :options="dnsAAAARecordStatusOptions"
                :reduce="s => s.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>
          </div>

          <div class="d-flex flex-lg-row flex-column flex-gap-2 mt-2">
            <!-- A Record IP Filter -->
            <div class="w-100">
              <CInput
                class="mb-0 search"
                type="search"
                placeholder="Filter by A record IP..."
                v-model="dnsARecordIpFilter"
                @input="debouncedSearch"
              />
            </div>
            <!-- AAAA Record IP Filter -->
            <div class="w-100">
              <CInput
                class="mb-0 search"
                type="search"
                placeholder="Filter by AAAA record IP..."
                v-model="dnsAAAARecordIpFilter"
                @input="debouncedSearch"
              />
            </div>
          </div>

          <div class="d-flex flex-gap-2 mt-2">
            <label>Show restaurants without subsite:</label>
            <CSwitch
              labelOn="YES"
              labelOff="NO"
              color="success"
              shape="pill"
              :checked.sync="dnsShowWithoutSubsite"
              @update:checked="inputFilter"
            />
          </div>

          <div class="d-flex flex-gap-2">
            <label>Show inactive restaurants:</label>
            <CSwitch
              labelOn="YES"
              labelOff="NO"
              color="success"
              shape="pill"
              :checked.sync="dnsShowInactive"
              @update:checked="inputFilter"
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>
    
    <CDataTable
      :items="items"
      :fields="fields"
      :loading="loading"
      :itemsPerPageSelect="{
        external: true,
        values: [5, 15, 25, 50, 100, 250, 500],
      }"
      :items-per-page.sync="itemsPerPage"
      hover
      striped
      :noItemsView="{
        noResults: 'No filtering results are available!',
        noItems: 'No restaurants found with a sub-web domain.',
      }"
      @pagination-change="paginationChange"
    >
      <template #restaurant="{item}">
        <td>
          <strong>
            <CLink :to="{ name: 'View Restaurant', params: { id: item.id } }">{{ item.label || item.restaurant_name }}</CLink>
          </strong>
          <small class="text-muted d-block">
            <CBadge
              v-if="item.domain_source && item.domain_source !== 'none'"
              :color="item.domain_source === 'primary' ? 'success' : 'info'"
              shape="pill"
              class="mr-1"
            >
              {{ item.domain_source === 'primary' ? 'Primary' : 'Subsite' }}
            </CBadge>
            {{ item.domain || 'No domain configured' }}
          </small>
        </td>
      </template>

      <template #dns_records="{item}">
        <td>
          <div v-if="item.dns_check_result && item.dns_check_result.checks" class="dns-checks">
            <!-- A Records -->
            <div class="dns-check-item">
              <div class="dns-check-header">
                <CIcon :name="getCheckIcon(item.dns_check_result.checks.A)" :class="getCheckIconClass(item.dns_check_result.checks.A)" />
                <span class="dns-type">A</span>
              </div>
              <div class="dns-check-content">
                <div v-if="item.dns_check_result.checks.A.found.length > 0" class="dns-values">
                  <span v-for="(ip, idx) in item.dns_check_result.checks.A.found" :key="idx" class="dns-value">{{ ip }}</span>
                </div>
                <small v-else class="text-muted">No records found</small>
              </div>
            </div>

            <!-- AAAA Records -->
            <div class="dns-check-item">
              <div class="dns-check-header">
                <CIcon :name="getCheckIcon(item.dns_check_result.checks.AAAA)" :class="getCheckIconClass(item.dns_check_result.checks.AAAA)" />
                <span class="dns-type">AAAA</span>
              </div>
              <div class="dns-check-content">
                <div v-if="item.dns_check_result.checks.AAAA.found.length > 0" class="dns-values">
                  <span v-for="(ip, idx) in item.dns_check_result.checks.AAAA.found" :key="idx" class="dns-value">{{ ip }}</span>
                </div>
                <small v-else class="text-muted">{{ item.dns_check_result.checks.AAAA.message }}</small>
              </div>
            </div>

            <!-- CNAME Records -->
            <div class="dns-check-item">
              <div class="dns-check-header">
                <CIcon :name="getCheckIcon(dnsCheck(item, 'CNAME'))" :class="getCheckIconClass(dnsCheck(item, 'CNAME'))" />
                <span class="dns-type">CNAME</span>
              </div>
              <div class="dns-check-content">
                <div v-if="dnsFound(item, 'CNAME').length > 0" class="dns-values">
                  <span v-for="(target, idx) in dnsFound(item, 'CNAME')" :key="idx" class="dns-value">{{ target }}</span>
                </div>
                <small v-else class="text-muted">{{ dnsMessage(item, 'CNAME', 'Not checked yet') }}</small>
              </div>
            </div>

            <!-- NS Records -->
            <div class="dns-check-item">
              <div class="dns-check-header">
                <CIcon :name="getCheckIcon(item.dns_check_result.checks.NS)" :class="getCheckIconClass(item.dns_check_result.checks.NS)" />
                <span class="dns-type">NS</span>
              </div>
              <div class="dns-check-content">
                <div v-if="item.dns_check_result.checks.NS.found.length > 0" class="dns-values">
                  <div class="dns-check-ns-badge">
                    <CBadge v-if="item.dns_check_result.checks.NS.is_ovh" color="success" shape="pill">OVH</CBadge>
                    <CBadge v-else-if="item.dns_check_result.checks.NS.is_custom_config" color="info" shape="pill">Configured Custom</CBadge>
                    <CBadge v-else color="warning" shape="pill">Custom</CBadge>
                  </div>

                  <span v-for="(ns, idx) in item.dns_check_result.checks.NS.found" :key="idx" class="dns-value">{{ ns }}</span>
                </div>
                <small v-else class="text-muted">No records found</small>
              </div>
            </div>
          </div>
          <pre v-else class="dns-record m-0">-</pre>
        </td>
      </template>

      <template #status="{item}">
        <td>
          <div v-if="item.dns_check_result" class="status-container">
            <div :class="['status-badge', getStatusClass(item.dns_check_result.status)]">
              <CIcon :name="getStatusIcon(item.dns_check_result.status)" />
              <span>{{ item.dns_check_result.message }}</span>
            </div>
            
            <!-- Detailed Status Messages -->
            <div v-if="item.dns_check_result.checks" class="status-details mt-2">
              <div v-for="type in dnsStatusDetailTypes(item)" :key="type" class="status-detail-item">
                <CIcon :name="getCheckIcon(item.dns_check_result.checks[type])" :class="getCheckIconClass(item.dns_check_result.checks[type])" size="sm" />
                <small>{{ type }}: {{ item.dns_check_result.checks[type].message }}</small>
              </div>
            </div>
          </div>
        </td>
      </template>

      <template #actions="{item}">
        <td>
          <div class="d-flex flex-column align-items-end flex-gap-2 w-auto">
            <template v-if="!['pending'].includes(item.dns_check_result?.status)">
              <!-- Point to Menute Hosting Button -->
              <!-- :disabled="isPointToMenuteDisabled(item)" -->
              <CButton 
                size="sm" 
                shape="pill" 
                color="dark" 
                @click="openDnsModal(item, 'point_to_menute')"
              >
                Point to Menute Hosting
              </CButton>

              <!-- Restore OVH DNS Button -->
              <CButton 
                size="sm" 
                shape="pill" 
                color="dark" 
                :disabled="isRestoreOvhDisabled(item)"
                @click="openDnsModal(item, 'restore_ovh')"
              >
                Restore OVH DNS
              </CButton>

              <!-- Use Custom Nameservers Button -->
              <CButton 
                size="sm" 
                shape="pill" 
                color="dark" 
                :disabled="isUseCustomNameserversDisabled(item)"
                @click="openDnsModal(item, 'use_custom_ns')"
              >
                Use Custom Nameservers
              </CButton>
            </template>

            <!-- Check DNS Button (only when domain exists) -->
            <CButton
              v-if="item.domain"
              size="sm" 
              shape="pill" 
              color="info" 
              :disabled="checkingDns[item.row_key]"
              @click="checkDns(item)"
            >
              <CSpinner v-if="checkingDns[item.row_key]" size="sm" class="mr-1 my-0" />
              <CIcon v-else name="cil-sync" class="mr-1 my-0" />
              Check DNS
            </CButton>

            <!-- Registration action for restaurants without subsite/domain -->
            <CButton 
              v-else
              size="sm" 
              shape="pill" 
              color="primary"
              @click="openRegistrationModal(item)"
            >
              Register Domain
            </CButton>
          </div>
        </td>
      </template>
    </CDataTable>
    
    <!-- Actions -->
    <CCard class="actions sticky-bottom">
      <CCardBody class="p-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <div>
            <h5 class="mt-1">Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span></h5>
          </div>

          <div class="d-flex flex-wrap align-items-center" style="gap: 0.5rem">
            <label class="mb-0">Dry run:</label>
            <CSwitch
              labelOn="YES"
              labelOff="NO"
              color="success"
              shape="pill"
              :checked.sync="domainExportDryRun"
              :disabled="domainExporting"
            />
            <CButton
              size="sm"
              color="warning"
              :disabled="domainExporting"
              @click="exportDomains"
            >
              <CSpinner v-if="domainExporting" size="sm" class="mr-1 my-0" />
              <CIcon v-else name="cil-cloud-upload" class="mr-1 my-0" />
              Export domains
            </CButton>
          </div>

          <div v-if="pages > 1" class="ml-auto">
            <CPagination
              align="center"
              :dots='false'
              :pages="pages"
              :active-page.sync="activePage"
            />
          </div>
        </div>

        <pre
          v-if="domainExportOutput.length > 0"
          :class="['domain-export-output mt-2 mb-0', domainExportSuccess === false ? 'domain-export-output-error' : '']"
        >{{ domainExportOutput.join('\n') }}</pre>
      </CCardBody>
    </CCard>

    <mc-spinner :opacity="0.8" v-show="loading" :mtop="(32 + mcSpinnerMarginTop) + 'px'" />
    
    <!-- DNS Management Modal -->
    <DnsManagementModal 
      :restaurant="selectedRestaurant" 
      :defaultOperation="selectedOperation"
      @operations-completed="onOperationsCompleted"
    />
    
    <!-- Domain Registration Modal -->
    <DomainRegistrationModal
      :restaurant="selectedRestaurantForRegister"
      @registered="onDomainRegistered"
    />
  </CCardBody>
</template>

<script>
import _debounce from 'lodash/debounce';
import { mapState } from "vuex";
import DnsManagementModal from './DnsManagementModal.vue';
import DomainRegistrationModal from './DomainRegistrationModal.vue';

export default {
  name: "DnsTable",
  components: {
    DnsManagementModal,
    DomainRegistrationModal,
  },
  data() {
    return {
      items: [],
      fields: [
        { key: 'restaurant',  label: 'Restaurant', _style: 'min-width: 200px; width: 280px;' },
        { key: 'dns_records', label: 'DNS Records', sorter: false, _style: 'min-width: 360px; width: 420px;' },
        { key: 'status',      label: 'Status', _style: 'min-width: 240px;' },
        { key: 'actions',     label: 'Actions', sorter: false, filter: false, _style: 'min-width: 180px; width: 180px; text-align: center;' },
      ],

      activePage: 1,
      items_per_page: null,
      pages: 1,
      total: 0,

      search: null,
      loading: false,
      mcSpinnerMarginTop: 0,
      dnsStatusOptions: [
        { label: 'All Status', value: null },
        { label: 'Correct', value: 'correct' },
        { label: 'Warning', value: 'warning' },
        { label: 'Error', value: 'error' },
        { label: 'Pending', value: 'pending' },
      ],
      dnsARecordStatusOptions: [
        { label: 'All A Records', value: null },
        { label: 'A Correct', value: 'correct' },
        { label: 'A Error', value: 'error' },
        { label: 'A Pending', value: 'pending' },
      ],
      dnsAAAARecordStatusOptions: [
        { label: 'All AAAA Records', value: null },
        { label: 'AAAA Correct', value: 'correct' },
        { label: 'AAAA Warning', value: 'warning' },
        { label: 'AAAA Error', value: 'error' },
        { label: 'AAAA Pending', value: 'pending' },
        { label: 'AAAA Optional', value: 'optional' },
      ],
      
      selectedRestaurant: null,
      selectedOperation: null,
      checkingDns: {}, // Track DNS checking status per restaurant
      dnsRecordTypes: ['A', 'AAAA', 'CNAME', 'NS'],
      domainExportDryRun: true,
      domainExporting: false,
      domainExportOutput: [],
      domainExportSuccess: null,

      // Domain Registration
      selectedRestaurantForRegister: null,
    };
  },
  computed: {
    ...mapState(['filters']),

    dnsShowWithoutSubsite: {
      get() { return this.filters.restaurants.dns.showWithoutSubsite; },
      set(value) { this.filters.restaurants.dns.showWithoutSubsite = value; }
    },
    dnsShowInactive: {
      get() { return this.filters.restaurants.dns.showInactive; },
      set(value) { this.filters.restaurants.dns.showInactive = value; }
    },
    dnsStatusFilter: {
      get() { return this.filters.restaurants.dns.statusFilter; },
      set(value) { this.filters.restaurants.dns.statusFilter = value; }
    },
    dnsARecordFilter: {
      get() { return this.filters.restaurants.dns.aRecordStatusFilter; },
      set(value) { this.filters.restaurants.dns.aRecordStatusFilter = value; }
    },
    dnsAAAARecordFilter: {
      get() { return this.filters.restaurants.dns.aaaaRecordStatusFilter; },
      set(value) { this.filters.restaurants.dns.aaaaRecordStatusFilter = value; }
    },
    dnsARecordIpFilter: {
      get() { return this.filters.restaurants.dns.aRecordIpFilter; },
      set(value) { this.filters.restaurants.dns.aRecordIpFilter = value; }
    },
    dnsAAAARecordIpFilter: {
      get() { return this.filters.restaurants.dns.aaaaRecordIpFilter; },
      set(value) { this.filters.restaurants.dns.aaaaRecordIpFilter = value; }
    },
    itemsPerPage: {
      get: function () { return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE) },
      set: function (newValue) { this.items_per_page = newValue }
    },
    reloadParams() { return [this.activePage]; },
    queryPage() { return parseInt(this.$route.query.page) || 1; },
  },
  watch: {
    reloadParams() {
      if(this.queryPage != this.activePage)
        this.$router.push({ name: 'DnsManagement', query: { page: this.activePage } });

      this.onTableChange();
    },
  },

  beforeMount() {
    this.debouncedSearch = _debounce(this.inputFilter, 500);
  },

  mounted() {
    this.activePage =  this.queryPage;
    this.fetchDnsData();

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    }
  },

  methods: {
    async fetchDnsData() {
      this.loading = true;
      try {
        const params = {
          page: this.activePage,
          itemsPerPage: this.itemsPerPage,
          search: this.search || null,
          country: this.$store.state.filters.country || null,
          show_without_subsite: this.dnsShowWithoutSubsite,
          status: this.dnsStatusFilter || null,
          a_record_status: this.dnsARecordFilter || null,
          aaaa_record_status: this.dnsAAAARecordFilter || null,
          a_record_ip: this.dnsARecordIpFilter || null,
          aaaa_record_ip: this.dnsAAAARecordIpFilter || null,
          show_inactive: this.dnsShowInactive,
        };

        const { data } = await this.$axios.get(this.$backend.RESTAURANT.DNS_MANAGEMENT.GET_ALL, { params });
        
        this.items = data.data;

        // Son sayfadan daha büyük bir queryPage çağrımı (?page=XXX) yapılırsa;
        if(data.current_page > data.last_page)
          this.activePage = 1;
        else {
          this.activePage = data.current_page;
          this.pages = data.last_page;
          this.total = data.total;
        }
      } catch (error) {
        this.$toast.error("Failed to load DNS information.");
        this.items = [];
        this.pages = 1;
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    onTableChange() { // Active Page güncellendiğinde çağrılıyor.
      this.fetchDnsData();
    },
    paginationChange(value) { // Items Per Page güncellendiğinde çağrılıyor.
      this.itemsPerPage = value;
      this.fetchDnsData();
    },

    // Filters
    countryFilter(value) {
      this.$store.commit("setCountryFilter", value);
      this.inputFilter();
    },
    inputFilter() {
      this.activePage = 1;
      this.fetchDnsData();
    },

    // Helper functions
    getStatusClass(status) {
      if (status === 'correct') return 'status-correct';
      if (status === 'error') return 'status-error';
      if (status === 'warning') return 'status-warning';
      return 'status-pending';
    },
    getStatusIcon(status) {
      if (status === 'correct') return 'cil-check-circle';
      if (status === 'error') return 'cil-x-circle';
      if (status === 'warning') return 'cil-warning';
      return 'cil-lan';
    },
    getCheckIcon(check) {
      if (!check) return 'cil-circle';
      if (check.status === 'correct') return 'cil-check-circle';
      if (check.status === 'error') return 'cil-x-circle';
      if (check.status === 'warning') return 'cil-warning';
      if (check.status === 'optional') return 'cil-info';
      return 'cil-circle';
    },
    getCheckIconClass(check) {
      if (!check) return '';
      if (check.status === 'correct') return 'text-success';
      if (check.status === 'error') return 'text-danger';
      if (check.status === 'warning') return 'text-warning';
      if (check.status === 'optional') return 'text-muted';
      return 'text-muted';
    },
    dnsCheck(item, type) {
      return item?.dns_check_result?.checks?.[type] || null;
    },
    dnsFound(item, type) {
      return this.dnsCheck(item, type)?.found || [];
    },
    dnsMessage(item, type, fallback) {
      return this.dnsCheck(item, type)?.message || fallback;
    },
    dnsStatusDetailTypes(item) {
      return this.dnsRecordTypes.filter((type) => {
        const status = this.dnsCheck(item, type)?.status;
        return status === 'error' || status === 'warning';
      });
    },
    
    // DNS Management Modal
    openDnsModal(restaurant, operation) {
      this.selectedRestaurant = restaurant;
      this.selectedOperation = operation;
      this.$store.state.dnsManagementModalShow = true;
    },
    onOperationsCompleted() {
      // Reload the DNS data after operations are completed
      this.fetchDnsData();
    },

    // Domain Registration Modal
    openRegistrationModal(restaurant) {
      this.selectedRestaurantForRegister = restaurant;
      this.$store.state.domainRegistrationModalShow = true;
    },
    onDomainRegistered() {
      this.fetchDnsData();
    },

    async exportDomains() {
      this.domainExporting = true;
      this.domainExportOutput = [];
      this.domainExportSuccess = null;

      try {
        const { data } = await this.$axios.post(
          this.$backend.RESTAURANT.DNS_MANAGEMENT.EXPORT_DOMAINS,
          { dry_run: this.domainExportDryRun }
        );

        this.domainExportSuccess = Boolean(data.success);
        this.domainExportOutput = data.output_lines?.length
          ? data.output_lines
          : [data.message || 'Restaurant domain export completed.'];
        this.$toast.success(this.domainExportDryRun
          ? 'Domain export dry-run completed.'
          : 'Domain export completed.');
      } catch (error) {
        const data = error.response?.data || {};
        this.domainExportSuccess = false;
        this.domainExportOutput = data.output_lines?.length
          ? data.output_lines
          : [data.message || 'Restaurant domain export failed.'];
        this.$toast.error(data.message || 'Restaurant domain export failed.');
      } finally {
        this.domainExporting = false;
      }
    },
    
    // Check if operations are disabled
    isPointToMenuteDisabled(item) {
      if (!item.dns_check_result || !item.dns_check_result.checks) return false;
      
      const aRecord = item.dns_check_result.checks.A;
      const aaaaRecord = item.dns_check_result.checks.AAAA;
      const cnameRecord = item.dns_check_result.checks.CNAME;
      
      // Disable if A, AAAA, and CNAME records are correct.
      return aRecord?.status === 'correct'
        && aaaaRecord?.status === 'correct'
        && cnameRecord?.status === 'correct';
    },
    isRestoreOvhDisabled(item) {
      if (!item.dns_check_result || !item.dns_check_result.checks) return false;
      
      const nsRecord = item.dns_check_result.checks.NS;
      
      // Disable if NS records are correct (pointing to OVH)
      return nsRecord?.status === 'correct' && nsRecord?.is_ovh === true;
    },
    isUseCustomNameserversDisabled(item) {
      if (!item.dns_check_result || !item.dns_check_result.checks) return false;
      
      const nsRecord = item.dns_check_result.checks.NS;
      return nsRecord?.is_custom_config === true;
    },
    
    // DNS Check
    async checkDns(item) {
      const rowKey = item.row_key || item.id;
      this.$set(this.checkingDns, rowKey, true);
      
      try {
        const { data } = await this.$axios.post(
          this.$backend.RESTAURANT.DNS_MANAGEMENT.CHECK_DNS.replace('{id}', item.id),
          { domain: item.domain }
        );
        
        if (data.success) {
          this.$toast.success('DNS check completed successfully!');
          // Update the item in the list with new DNS check result
          const index = this.items.findIndex(i => (i.row_key || i.id) === rowKey);
          if (index !== -1) {
            this.$set(this.items[index], 'dns_check_result', data.dns_check_result);
          }
        } else {
          this.$toast.error(data.message || 'DNS check failed.');
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to check DNS.';
        this.$toast.error(errorMessage);
      } finally {
        this.$set(this.checkingDns, rowKey, false);
      }
    },
  },
};
</script>

<style>
.search input {
  font-size: 1rem !important;
  line-height: 1.7 !important;
}
</style>

<style scoped>
.dns-record {
  background-color: #f8f9fa;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  padding: 0.75rem;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: monospace;
  font-size: 0.875rem;
}

.domain-export-output {
  max-height: 9rem;
  overflow: auto;
  background-color: #f8f9fa;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  padding: 0.5rem;
  white-space: pre-wrap;
  font-size: 0.8rem;
}

.domain-export-output-error {
  border-color: #dc3545;
  color: #8a1f2d;
  background-color: rgba(220, 53, 69, 0.06);
}

/* DNS Checks */
.dns-checks {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dns-check-item {
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  border: 1px solid #e1e1e1;
  border-radius: 6px;
  padding: 0.375rem 0.5rem;
  transition: all 0.2s ease;
}

.dns-check-item:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.dns-check-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0;
}

.dns-check-header .c-icon {
  font-size: 1.1rem;
}

.dns-type {
  font-weight: 600;
  font-family: monospace;
  font-size: 0.875rem;
  color: #495057;
  min-width: 48px;
}

.dns-check-content {
  margin-left: 1.6rem;
}

.dns-values {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dns-value {
  font-family: monospace;
  font-size: 0.875rem;
  color: #212529;
  padding: 0.125rem 0.5rem;
  background-color: #fff;
  border-radius: 3px;
  border: 1px solid #dee2e6;
  display: inline-block;
  width: fit-content;
}

/* Status Container */
.status-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 500;
  gap: 0.5rem;
  min-width: 120px;
}

.status-badge .c-icon {
  font-size: 1.25rem;
}

.status-correct {
  background-color: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.3);
  color: #1e7e34;
}

.status-error {
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: #dc3545;
}

.status-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  color: #d39e00;
}

.status-pending {
  background-color: rgba(108, 117, 125, 0.1);
  border: 1px solid rgba(108, 117, 125, 0.3);
  color: #495057;
}

/* Status Details */
.status-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.status-detail-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #dc3545;
}

.status-detail-item .c-icon {
  flex-shrink: 0;
}

.status-detail-item small {
  font-size: 0.8rem;
  line-height: 1.3;
}
</style>
